import {branch, mapProps, renderComponent, withProps} from 'recompose'

import {skipToken} from '@reduxjs/toolkit/query'
import compose from 'lodash/flowRight'
import type {ConnectedProps} from 'react-redux'
import {connect} from 'react-redux'
import {useParams} from 'react-router-dom'

import withErrorPage from '../../../hocs/withErrorPage'
import withHook from '../../../hocs/withHook'
import {withQueryParam} from '../../../hooks/routes'
import type {State} from '../../../reducers/types'
import {getSingleBuildArg} from '../../../rest/builds'
import {getBuildLocator} from '../../../rest/locators'
import {getExtensionEndpoint} from '../../../selectors'
import {restApi} from '../../../services/rest'
import type {BuildId} from '../../../types'
import {toBuildId} from '../../../types'
import {extractError} from '../../../utils/extractError'
import {withAvailabilityResponse} from '../../common/AvailabilityErrorPage/AvailabilityErrorPage.hocs'
import {getAvailabilityErrorMessage} from '../../common/AvailabilityErrorPage/AvailabilityErrorPage.utils'
import ErrorPage from '../../common/ErrorPage/ErrorPage'

import CompareBuildsPage from './CompareBuildsPage'

type ReduxOwnProps = {
  sourceId: BuildId
  compareWithId: BuildId | null | undefined
}
type StateProps = {
  extensionEnabled: boolean
}

const mapStateToProps = (state: State): StateProps => ({
  extensionEnabled: Boolean(getExtensionEndpoint(state, 'builds-diff')),
})

const connector = connect(mapStateToProps)

function useErrors({sourceId, compareWithId}: ReduxOwnProps) {
  const {sourceBuildError} = restApi.endpoints.getBuildNormalized.useQuery(
    getSingleBuildArg(getBuildLocator(sourceId)),
    {
      selectFromResult: ({error}) => ({
        sourceBuildError: error,
      }),
    },
  )
  const {compareBuildError} = restApi.endpoints.getBuildNormalized.useQuery(
    compareWithId != null ? getSingleBuildArg(getBuildLocator(compareWithId)) : skipToken,
    {
      selectFromResult: ({error}) => ({
        compareBuildError: error,
      }),
    },
  )
  return {
    sourceBuildError: extractError(sourceBuildError),
    compareBuildError: extractError(compareBuildError),
  }
}

// compose is only well typed for 2-7 arguments
export default compose(
  compose(
    withHook(useParams),
    withQueryParam('withBuildId'),
    mapProps(({buildIdSource, withBuildId}) => ({
      sourceId: toBuildId(buildIdSource),
      compareWithId: withBuildId != null ? toBuildId(withBuildId) : null,
    })),
    connector,
    branch(
      (props: ReduxOwnProps & ConnectedProps<typeof connector>) => !props.extensionEnabled,
      renderComponent(() => (
        <ErrorPage error={new Error('Plugin teamcity-compare-builds is not enabled')} />
      )),
    ),
    withProps({
      getErrorMessage: getAvailabilityErrorMessage,
    }),
    withAvailabilityResponse,
  ),
  withHook(useErrors),
  withErrorPage(
    ({
      sourceBuildError,
      sourceId,
      compareBuildError,
      compareWithId,
    }: ReduxOwnProps & ConnectedProps<typeof connector> & ReturnType<typeof useErrors>) => {
      if (sourceBuildError != null) {
        return (
          getAvailabilityErrorMessage({
            error: sourceBuildError,
            buildId: sourceId,
          }) || sourceBuildError
        )
      }

      if (compareBuildError != null) {
        return (
          getAvailabilityErrorMessage({
            error: compareBuildError,
            buildId: compareWithId,
          }) || compareBuildError
        )
      }

      return null
    },
  ),
)(CompareBuildsPage)
